import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Printer } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { localDay, money, shortDate } from "@/lib/invoice";
import DateField from "@/components/DateField";
import { Button } from "@/components/ui/button";

export default function DepositSlip() {
  const [search, setSearch] = useSearchParams();
  const date = search.get("date") || localDay();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setData(null); setError("");
    api.get("/deposit-slip", { params: { date } }).then((r) => setData(r.data)).catch((e) => setError(formatError(e)));
  }, [date]);

  useEffect(() => { document.title = `Deposit slip ${shortDate(date)}`; }, [date]);

  const s = data?.settings || {};
  return (
    <div className="min-h-screen bg-white text-black" data-testid="deposit-slip-page">
      <div className="print:hidden flex flex-wrap items-center gap-3 border-b bg-neutral-100 px-6 py-3">
        <span className="text-sm font-medium">Payments marked paid on</span>
        <div className="w-52 [&_button]:bg-white [&_button]:text-black"><DateField id="deposit-date" value={`${date} 00:00:00`} onChange={(v) => v && setSearch({ date: v.slice(0, 10) })} /></div>
        <Button onClick={() => window.print()} disabled={!data?.rows.length} data-testid="deposit-print-button"><Printer size={15} /> Print</Button>
      </div>
      <div className="mx-auto max-w-3xl p-10">
        {error && <p className="text-red-600" data-testid="deposit-error">{error}</p>}
        {!data && !error && <Loader2 className="animate-spin" />}
        {data && (
          <>
            <div className="flex justify-between items-start border-b-2 border-black pb-3">
              <div>
                <h1 className="text-2xl font-bold">{s.company_name}</h1>
                <p className="text-sm">{s.address_line1}</p>
                <p className="text-sm">{s.address_line2}</p>
              </div>
              <div className="text-right">
                <h2 className="text-xl font-bold tracking-wide">DEPOSIT SLIP</h2>
                <p className="text-sm" data-testid="deposit-date-label">Date: {shortDate(data.date)}</p>
              </div>
            </div>
            <table className="w-full mt-6 text-sm border-collapse">
              <thead>
                <tr className="border-b border-black text-left">
                  {["#", "Check # / note", "Customer", "Invoice #", "Amount"].map((h) => <th key={h} className={`py-2 ${h === "Amount" ? "text-right" : ""}`}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={r.id} className="border-b border-neutral-300" data-testid={`deposit-row-${i}`}>
                    <td className="py-1.5">{i + 1}</td>
                    <td className="py-1.5 font-mono">{r.payment_note || "—"}</td>
                    <td className="py-1.5">{r.customer}</td>
                    <td className="py-1.5 font-mono">{r.number}</td>
                    <td className="py-1.5 text-right font-mono">{money(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-black font-bold">
                  <td colSpan={4} className="py-2">{data.rows.length} payment{data.rows.length === 1 ? "" : "s"}</td>
                  <td className="py-2 text-right font-mono" data-testid="deposit-total">{money(data.total)}</td>
                </tr>
              </tfoot>
            </table>
            {!data.rows.length && <p className="mt-6 text-neutral-500" data-testid="deposit-empty">No invoices were marked paid on this day.</p>}
            <div className="mt-16 grid grid-cols-2 gap-10 text-sm">
              <p className="border-t border-black pt-1">Deposited by</p>
              <p className="border-t border-black pt-1">Bank / date</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
